import {
  QueryKeyHashFunction,
  UseMutationOptions,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { JsonValue } from '@components/json-editor';
import { showError, showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { sanitizeJSON } from '@utils/collections';

import {
  Authentication,
  DuplicateEndpointData,
  DuplicateIntegrationData,
  EditPollingVisibility,
  EditPollingVisibilityType,
  Integration,
  IntegrationEndpoint,
  IntegrationEndpointUpdateType,
  IntegrationEndpointVariable,
  IntegrationPollingType,
  IntegrationWebhook,
  IntegrationWebhookCreateRemoveType,
  IntegrationWebhookUpdateType,
  ListSyncTestRequest,
  authenticationTest,
  createIntegration,
  createIntegrationEndpoint,
  createIntegrationWebhook,
  deleteIntegration,
  deleteIntegrationEndpoint,
  deleteIntegrationWebhook,
  duplicateIntegration,
  duplicateIntegrationEndpoint,
  duplicateIntegrationWebhook,
  editIntegration,
  generateJqQuery,
  getIntegrationWebhook,
  integrationTestRequest,
  listPollingEndpoints,
  loadIntegration,
  loadIntegrationEndpoint,
  loadIntegrationJourneys,
  loadIntegrationTravellerActionFile,
  loadIntegrationWebhookLogs,
  loadIntegrations,
  loadJourneyActionIntegrationEndpoint,
  loadJourneyIntegrationEndpoints,
  loadJourneyIntegrationWebhooks,
  loadTravellerActionIntegrationEndpoint,
  loadTravellerJourneyIntegrationEndpoint,
  subscribeIntegrationWebhook,
  unsubscribeIntegrationWebhook,
  updateIntegrationAuthentication,
  updateIntegrationEndpoint,
  updateIntegrationEndpointDefaultFiles,
  updateIntegrationEndpointExampleFiles,
  updateIntegrationWebhook,
  validateJqQuery,
} from './requests';

const getIntegrationsQueryKey = (queryString?: string) =>
  ['integrations', queryString].filter(Boolean);

interface IntegrationListOptions {
  queryString?: string;
  isTested?: boolean;
}
export const useIntegrationList = ({
  queryString = '',
  isTested = false,
}: IntegrationListOptions = {}) => {
  return useQuery({
    queryKey: getIntegrationsQueryKey(queryString),
    queryFn: () => loadIntegrations(queryString, isTested ?? false),
    keepPreviousData: true,
  });
};

export const useIntegration = (shortId: string) => {
  return useQuery({
    queryKey: ['integrations', shortId],
    queryFn: () => loadIntegration(shortId),
  });
};

export const useCreateIntegration = () => {
  return useMutation({
    mutationFn: createIntegration,
  });
};

export const useEditIntegration = (onSuccess?: () => void) => {
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: editIntegration,
    onSuccess: () => {
      invalidateIntegrations();
      onSuccess?.();
    },
  });
};

export const useDeleteIntegration = () => {
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: deleteIntegration,
    onSuccess: () => invalidateIntegrations(),
  });
};

export const useIntegrationEndpoint = (
  shortId: string,
  options: { cacheTime?: number; queryKeyHashFn?: QueryKeyHashFunction<string[]> } = {}
) => {
  return useQuery({
    enabled: Boolean(shortId),
    queryKey: ['integrations', 'endpoint', shortId],
    queryFn: () => loadIntegrationEndpoint(shortId),
    ...options,
  });
};

export const useDeleteIntegrationEndpoint = (integrationShortId: string, shortId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteIntegrationEndpoint(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations', integrationShortId]);
    },
  });
};

type IntegrationErrorResponse = AxiosError<{ url: string[] }>;

export const useCreateIntegrationEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createIntegrationEndpoint,
    onSuccess: (data) => queryClient.invalidateQueries(['integrations', data.integration]),
    onError: (error: IntegrationErrorResponse) => {
      if (error.response?.data.url) {
        showError(error?.response?.data.url[0]);
      }
    },
  });
};

export const useUpdateIntegrationEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { shortId: string; data: IntegrationEndpointUpdateType }) =>
      updateIntegrationEndpoint(params.shortId, params.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['integrations', data.integration]);
      queryClient.invalidateQueries(['integrations', 'endpoint', data.shortId]);
    },
    onError: (error: IntegrationErrorResponse) => {
      if (error.response?.data.url) {
        showError(error?.response?.data.url[0]);
      }
    },
  });
};

export const useUpdateIntegrationEndpointExampleFiles = () => {
  const { invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (params: { shortId: string; data: FormData }) =>
      updateIntegrationEndpointExampleFiles(params.shortId, params.data),
    onSuccess: () => invalidateIntegrations(),
  });
};

export const useUpdateIntegrationEndpointDefaultFiles = () => {
  const { invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (params: { shortId: string; data: FormData }) =>
      updateIntegrationEndpointDefaultFiles(params.shortId, params.data),
    onSuccess: () => invalidateIntegrations(),
  });
};

export const useIntegrationJourneys = (shortId: string) => {
  return useQuery({
    queryKey: ['integration-journeys', shortId],
    queryFn: () => loadIntegrationJourneys(shortId),
  });
};

export const useUpdateIntegrationAuthentication = (
  shortId: string,
  options?: UseMutationOptions<AxiosResponse, AxiosError, Authentication>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => {
      return updateIntegrationAuthentication(shortId, data);
    },
    onSuccess: () => queryClient.invalidateQueries(['integrations', shortId]),
  });
};

export const useIntegrationTestRequest = (shortId: string) => {
  const { invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: async (data: { variables: IntegrationEndpointVariable[] }) => {
      const response = await integrationTestRequest(shortId, data);
      return sanitizeJSON(response);
    },
    onSuccess: () => invalidateIntegrations(),
  });
};

export const useAuthenticationTest = () => {
  return useMutation({
    mutationFn: authenticationTest,
  });
};

export const useJourneyIntegrationEndpoints = (shortId: string) => {
  return useQuery({
    queryKey: ['journey', shortId, 'integration-endpoints'],
    queryFn: () => loadJourneyIntegrationEndpoints(shortId),
    enabled: Boolean(shortId),
  });
};

export const useJourneyActionIntegrationEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['journey-action-integration-endpoints', shortId],
    queryFn: () => loadJourneyActionIntegrationEndpoint(shortId),
  });
};

export const useTravellerJourneyIntegrationEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-journey-integration-endpoints', shortId],
    queryFn: () => loadTravellerJourneyIntegrationEndpoint(shortId),
  });
};

export const useTravellerActionIntegrationEndpoint = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-action-integration-endpoints', shortId],
    queryFn: () => loadTravellerActionIntegrationEndpoint(shortId),
  });
};

const integrationsQueryKey = ['integrations'];
const getIntegrationQueryKey = (shortId?: string) => ['integrations', shortId].filter(Boolean);
const getWebhookQueryKey = (shortId?: string) => ['integrations-webhook', shortId].filter(Boolean);
const getWebhookLogsQueryKey = (shortId?: string) =>
  ['integrations-webhook', shortId, 'logs'].filter(Boolean);

export const useInvalidateIntegrations = () => {
  const queryClient = useQueryClient();

  const invalidateIntegrations = () => queryClient.invalidateQueries(integrationsQueryKey);

  const invalidateIntegration = (shortId: string) =>
    queryClient.invalidateQueries(getIntegrationQueryKey(shortId));
  const invalidateWebhook = (shortId: string) =>
    queryClient.invalidateQueries(getWebhookQueryKey(shortId));
  return { invalidateIntegrations, invalidateIntegration, invalidateWebhook };
};

export const useIntegrationWebhook = (shortId: string) => {
  return useQuery({
    queryKey: getWebhookQueryKey(shortId),
    queryFn: () => getIntegrationWebhook(shortId),
    enabled: Boolean(shortId),
  });
};

export const useCreateIntegrationWebhook = () => {
  const { invalidateIntegration } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: createIntegrationWebhook,
    onSuccess: (data) => {
      invalidateIntegration(data.integration);
    },
  });
};

export const useUpdateIntegrationWebhook = (onSuccess?: () => void) => {
  const { invalidateWebhook, invalidateIntegration } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (params: { shortId: string; data: IntegrationWebhookUpdateType }) =>
      updateIntegrationWebhook(params.shortId, params.data),
    onSuccess: (data) => {
      invalidateWebhook(data.shortId);
      invalidateIntegration(data.integration);
      onSuccess?.();
    },
  });
};

export const useDeleteIntegrationWebhook = (shortId: string) => {
  const { invalidateIntegrations, invalidateWebhook } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: () => deleteIntegrationWebhook(shortId),
    onSuccess: () => {
      invalidateWebhook(shortId);
      invalidateIntegrations();
    },
  });
};

type JSONData = Record<string, JsonValue>;

export const useSubscribeIntegrationWebhook = (shortId: string) => {
  const { invalidateWebhook, invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (data: JSONData) => subscribeIntegrationWebhook(shortId, data),
    onSuccess: () => {
      invalidateWebhook(shortId);
      invalidateIntegrations();
    },
  });
};

export const useUnsubscribeIntegrationWebhook = (shortId: string) => {
  const { invalidateWebhook, invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (data: IntegrationWebhookCreateRemoveType) =>
      unsubscribeIntegrationWebhook(shortId, data),
    onSuccess: () => {
      invalidateWebhook(shortId);
      invalidateIntegrations();
    },
  });
};

export const useIntegrationWebhookLogs = (shortId: string) => {
  return useQuery({
    queryKey: getWebhookLogsQueryKey(shortId),
    queryFn: () => loadIntegrationWebhookLogs(shortId),
  });
};

export const useJourneyIntegrationWebhook = (shortId: string) => {
  return useQuery({
    queryKey: ['journey', shortId, 'integration-webhooks'],
    queryFn: () => loadJourneyIntegrationWebhooks(shortId),
    enabled: Boolean(shortId),
  });
};

export const useIntegrationTravellerActionFile = (shortId: string) => {
  return useQuery({
    queryKey: ['traveller-action-integration-file', shortId],
    queryFn: () => loadIntegrationTravellerActionFile(shortId),
  });
};

export const useListSyncTestRequest = (integrationShortId: string) => {
  const { invalidateIntegrations } = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (data: { url: string }) => ListSyncTestRequest(integrationShortId, data),
    onSuccess: () => invalidateIntegrations(),
  });
};

export const useDuplicateIntegrationEndpoint = (endpoint: IntegrationEndpoint<string>) => {
  const { invalidateIntegration } = useInvalidateIntegrations();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: (data: DuplicateEndpointData) =>
      duplicateIntegrationEndpoint(endpoint.shortId, data),
    onSuccess: () => {
      invalidateIntegration(endpoint.integration);
      showSuccess(t('endpointDuplicated'));
    },
  });
};

export const useDuplicateIntegrationWebhook = (webhhook: IntegrationWebhook) => {
  const { invalidateIntegration } = useInvalidateIntegrations();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: (data: DuplicateEndpointData) =>
      duplicateIntegrationWebhook(webhhook.shortId, data),
    onSuccess: () => {
      invalidateIntegration(webhhook.integration);
      showSuccess(t('webhookDuplicated'));
    },
  });
};

export const useDuplicateIntegration = (integration: Integration) => {
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: (data: DuplicateIntegrationData) => duplicateIntegration(integration.shortId, data),
    onSuccess: () => {
      showSuccess(t('integrationDuplicated'));
      queryClient.invalidateQueries(getIntegrationsQueryKey());
    },
  });
};

export const useEditPollingVisibility = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidateIntegrations } = useInvalidateIntegrations();

  return useMutation({
    mutationFn: (data: EditPollingVisibilityType) => EditPollingVisibility(data),
    onSuccess: () => {
      invalidateIntegrations();
      showSuccess(t('pollVisibilitySuccess'));
    },
  });
};

export const useListPollingEndpoints = (
  journeyId: string,
  options?: UseQueryOptions<IntegrationPollingType[]>
) => {
  return useQuery({
    queryKey: ['integrations', journeyId, 'polling-endpoints'],
    queryFn: () => listPollingEndpoints(journeyId),
    keepPreviousData: true,
    onSuccess: (integrations) => {
      options?.onSuccess?.(integrations);
    },
  });
};

export const useValidateJqQuery = () => {
  return useMutation({
    mutationFn: validateJqQuery,
  });
};

export const useGenerateJqQuery = () => {
  return useMutation({
    mutationFn: generateJqQuery,
  });
};

const locale = {
  en: {
    endpointDuplicated: 'Endpoint duplicated',
    webhookDuplicated: 'Webhook duplicated',
    pollVisibilitySuccess: 'Polling visibility updated successfully',
    integrationDuplicated: 'Integration duplicated',
  },
};
